import React from 'react';
import { Link } from 'react-router-dom';

import { ZoneStayPeriod } from 'src/features/stay/types';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { useIntl } from 'react-intl';
import { useGetStayDuration } from 'src/hooks/useGetStayDuration';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { PATH } from 'src/features/navigation/utils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { Separator } from 'src/registry/ui/Separator';
import { formatTimestampToCustomId } from 'src/features/visitor/utils/timestampToId';

import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { GATEWAY_PASSES_PANEL, STAY_PERIODS_PANEL } from 'src/features/gatewayPass/utils/constants';
import SidePanelAttribute from 'src/features/stay/components/sidePanel/SidePanelAttribute';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

type ZoneStayPeriodProp = {
    zoneStayPeriod: ZoneStayPeriod[];
    setSelectedZoneGatewayPass: React.Dispatch<React.SetStateAction<string>>;
};

const ZonesStayPeriod = ({ zoneStayPeriod, setSelectedZoneGatewayPass }: ZoneStayPeriodProp) => {
    const { formatMessage, locale } = useIntl();
    const { getDurationFromStartDateToEndDate } = useGetStayDuration();

    const accessZonesPermission = useHasPermissions([PERMISSION.ACCESS_ZONES]);

    const { openPanel, closePanel } = useSidePanel();

    if (zoneStayPeriod?.length === 0) return formatMessage(visitorMessages.noDataAvailable);

    return (
        <>
            {zoneStayPeriod?.map((zone) => {
                return (
                    <>
                        <div className="mt-4 grid gap-0.5">
                            <SidePanelBodyAttribute className="flex flex-row items-center gap-2">
                                <SidePanelBodyAttributeTitle>
                                    {formatMessage(visitorMessages.parkingZone)}
                                </SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={zone.zone} fallbackMessage="-">
                                    {accessZonesPermission && zone.zone ? (
                                        <div className="text-primary-600">
                                            <Link to={`/${PATH.ADMINISTRATION}/zone/${zone.zone.id}`}>
                                                {zone.zone?.translations.find((t) => t.language === locale)?.name ??
                                                    '-'}
                                                <FontAwesomeIcon icon={ICON_MAP.ARROW_UP_RIGHT} />
                                            </Link>
                                        </div>
                                    ) : (
                                        <div>
                                            {zone.zone?.translations.find((t) => t.language === locale)?.name ?? '-'}
                                        </div>
                                    )}
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>

                            <SidePanelAttribute
                                label={formatMessage(visitorMessages.entryTime)}
                                value={getFormattedDateTimeForDetailPanel(zone?.entry_pass?.time)}
                                className="flex flex-row items-center gap-2"
                            />

                            <SidePanelAttribute
                                label={formatMessage(visitorMessages.entryPoint)}
                                value={
                                    zone?.entry_pass?.gateway?.translations?.find((t) => t.language === locale)?.name ??
                                    '-'
                                }
                                className="flex flex-row items-center gap-2"
                            />

                            <SidePanelBodyAttribute className="flex flex-row items-center gap-2">
                                <SidePanelBodyAttributeTitle>
                                    {formatMessage(visitorMessages.entryPass)}
                                </SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={zone.entry_pass} fallbackMessage="-">
                                    <div
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            closePanel(STAY_PERIODS_PANEL);
                                            openPanel(GATEWAY_PASSES_PANEL);
                                            setSelectedZoneGatewayPass(zone?.entry_pass?.id);
                                        }}
                                        className="text-primary-600 hover:text-primary-600 cursor-pointer font-normal"
                                    >
                                        {formatTimestampToCustomId(zone?.entry_pass?.time)}
                                    </div>
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>

                            <SidePanelAttribute
                                label={formatMessage(visitorMessages.exitTime)}
                                value={getFormattedDateTimeForDetailPanel(zone?.entry_pass?.time)}
                                className="flex flex-row items-center gap-2"
                            />

                            <SidePanelAttribute
                                label={formatMessage(visitorMessages.exitPoint)}
                                value={
                                    zone?.exit_pass?.gateway?.translations?.find((t) => t.language === locale)?.name ??
                                    '-'
                                }
                            />

                            <SidePanelBodyAttribute className="flex flex-row items-center gap-2">
                                <SidePanelBodyAttributeTitle>
                                    {formatMessage(visitorMessages.exitPass)}
                                </SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={zone.exit_pass} fallbackMessage="-">
                                    <div
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            closePanel(STAY_PERIODS_PANEL);
                                            openPanel(GATEWAY_PASSES_PANEL);
                                            setSelectedZoneGatewayPass(zone?.exit_pass?.id);
                                        }}
                                        className="text-primary-600 hover:text-primary-600 cursor-pointer font-normal"
                                    >
                                        {formatTimestampToCustomId(zone?.exit_pass?.time)}
                                    </div>
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>

                            <SidePanelAttribute
                                label={formatMessage(visitorMessages.duration)}
                                value={getDurationFromStartDateToEndDate({
                                    startDate: zone.start_date,
                                    endDate: zone.end_date ?? undefined,
                                })}
                                className="flex flex-row items-center gap-2"
                            />

                            {zoneStayPeriod?.length > 1 && <Separator className="mt-2" />}
                        </div>
                    </>
                );
            })}
        </>
    );
};

export default ZonesStayPeriod;
